import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { useMutation, useQuery } from '@api/hooks'
import {
	faChevronDown,
	faChevronRight,
	faCircleExclamation,
	faPenToSquare,
	faWandMagicSparkles,
	faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { faPaperPlaneTop, faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import {
	Alert,
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	IconButton,
	keyframes,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import ArtemisAgentApi, {
	AgentConversation,
	AgentItem,
	AgentToolItem,
	ArtemisAgentKeys,
} from '@services/artemisAgent.api'
import { useQueryClient } from '@tanstack/react-query'
import { getTestId } from '@utils/helpers'
import { useLocation, useNavigate } from 'react-router-dom'

/** The name users see. Kept separate from the internal "artemis agent" naming. */
const ARTY_NAME = 'Arty'

const COLLAPSED_WIDTH = 44
const MIN_WIDTH = 320
const MAX_WIDTH = 640
const DEFAULT_WIDTH = 400

const SUGGESTIONS = [
	{
		id: 'create-basket',
		label: 'Create a new market basket',
		prompt: 'Create a new market basket.',
	},
]

/** What the thread shows; built by the API, so the panel never handles the model's history. */
export type ArtemisAgentItem = AgentItem

export type ArtemisAgentToolItem = AgentToolItem

export type ArtemisAgentConfirmation = {
	toolName: string
	summary: string
	arguments: Record<string, unknown>
}

const parse = (content: string | null | undefined) => {
	try {
		return JSON.parse(content ?? '')
	} catch {
		return null
	}
}

const humanize = (name: string) =>
	name.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const bounce = keyframes`
	0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
	40% { transform: scale(1); opacity: 1; }
`

function MessageBubble({ role, content }: { role: 'user' | 'assistant'; content: string }) {
	const isUser = role === 'user'
	return (
		<Box
			data-testid="artemis-agent-message"
			data-role={role}
			sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
		>
			<Paper
				elevation={0}
				sx={{
					px: 1.5,
					py: 1,
					maxWidth: '85%',
					borderRadius: 2,
					bgcolor: isUser ? 'primary.main' : 'grey.100',
					color: isUser ? 'primary.contrastText' : 'text.primary',
					border: (t) => (isUser ? 'none' : `1px solid ${t.palette.divider}`),
				}}
			>
				<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
					{content}
				</Typography>
			</Paper>
		</Box>
	)
}

/** A tool's output is written for the model, so it sits behind a disclosure under a friendly label. */
function ToolActivityLine({ item }: { item: ArtemisAgentToolItem }) {
	const [open, setOpen] = useState(false)
	const details = {
		mt: 0.5,
		ml: 3.5,
		p: 1,
		bgcolor: 'grey.50',
		border: 1,
		borderColor: 'divider',
		borderRadius: 1,
	}
	const mono = { m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: 'text.secondary' }

	return (
		<Box data-testid="artemis-agent-tool-activity">
			<Stack
				direction="row"
				alignItems="center"
				spacing={0.5}
				sx={{ color: item.isError ? 'error.main' : 'text.secondary', cursor: 'pointer' }}
				onClick={() => setOpen((v) => !v)}
			>
				<IconButton size="small" sx={{ p: 0.25 }} aria-label="expand tool detail">
					<FontAwesomeIcon
						icon={open ? faChevronDown : faChevronRight}
						style={{ fontSize: '0.7rem' }}
					/>
				</IconButton>
				<FontAwesomeIcon
					icon={item.isError ? faCircleExclamation : faWandMagicSparkles}
					style={{ fontSize: '0.85rem' }}
				/>
				<Typography variant="caption">{humanize(item.toolName)}</Typography>
			</Stack>
			<Collapse in={open} unmountOnExit>
				<Stack spacing={0.75} sx={details}>
					{item.arguments && (
						<Box>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
								Arguments
							</Typography>
							<Typography component="pre" variant="caption" sx={mono}>
								{JSON.stringify(parse(item.arguments) ?? item.arguments, null, 2)}
							</Typography>
						</Box>
					)}
					<Box>
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
							Output
						</Typography>
						<Typography component="pre" variant="caption" sx={mono}>
							{item.output
								? JSON.stringify(parse(item.output) ?? item.output, null, 2)
								: '(no output)'}
						</Typography>
					</Box>
				</Stack>
			</Collapse>
		</Box>
	)
}

function ThinkingIndicator() {
	return (
		<Stack
			direction="row"
			spacing={1}
			alignItems="center"
			data-testid="artemis-agent-thinking"
			sx={{ pl: 1 }}
		>
			<Stack direction="row" spacing={0.5} alignItems="center">
				{[0, 1, 2].map((i) => (
					<Box
						key={i}
						sx={{
							width: 6,
							height: 6,
							borderRadius: '50%',
							bgcolor: 'primary.main',
							animation: `${bounce} 1.2s infinite ease-in-out`,
							animationDelay: `${i * 0.15}s`,
						}}
					/>
				))}
			</Stack>
			<Typography variant="caption" color="text.secondary">
				Thinking…
			</Typography>
		</Stack>
	)
}

function ConfirmationCard({
	confirmation,
	loading,
	onConfirm,
	onCancel,
}: {
	confirmation: ArtemisAgentConfirmation
	loading: boolean
	onConfirm: () => void
	onCancel: () => void
}) {
	const [showDetails, setShowDetails] = useState(false)

	return (
		<Paper
			data-testid="artemis-agent-confirmation"
			elevation={0}
			sx={{
				p: 1.5,
				borderRadius: 2,
				border: (t) => `1px solid ${t.palette.warning.light}`,
				bgcolor: 'warning.50',
			}}
		>
			<Stack spacing={1}>
				<Typography variant="caption" color="text.secondary">
					Confirm {confirmation.toolName.replace(/_/g, ' ')}
				</Typography>
				<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
					{confirmation.summary}
				</Typography>
				<Stack
					direction="row"
					alignItems="center"
					spacing={0.5}
					sx={{ color: 'text.secondary', cursor: 'pointer' }}
					onClick={() => setShowDetails((v) => !v)}
				>
					<FontAwesomeIcon
						icon={showDetails ? faChevronDown : faChevronRight}
						style={{ fontSize: '0.7rem' }}
					/>
					<Typography variant="caption">Details</Typography>
				</Stack>
				<Collapse in={showDetails} unmountOnExit>
					<Box
						sx={{
							p: 1,
							bgcolor: 'grey.50',
							border: 1,
							borderColor: 'divider',
							borderRadius: 1,
							overflow: 'auto',
							maxHeight: 200,
						}}
					>
						<Typography
							component="pre"
							variant="caption"
							sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
						>
							{JSON.stringify(confirmation.arguments, null, 2)}
						</Typography>
					</Box>
				</Collapse>
				<Stack direction="row" spacing={1} justifyContent="flex-end">
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={onCancel}
						disabled={loading}
						data-testid="artemis-agent-cancel"
					>
						Cancel
					</Button>
					<Button
						size="small"
						variant="contained"
						onClick={onConfirm}
						loading={loading}
						data-testid="artemis-agent-confirm"
					>
						Confirm
					</Button>
				</Stack>
			</Stack>
		</Paper>
	)
}

/** A drag handle on the panel's right edge. The width is read when the drag starts so it never jumps. */
function ResizeHandle({ width, onResize }: { width: number; onResize: (next: number) => void }) {
	const drag = useRef<{ startX: number; startWidth: number } | null>(null)

	const move = useCallback(
		(e: MouseEvent) => {
			if (!drag.current) return
			onResize(
				Math.min(
					MAX_WIDTH,
					Math.max(MIN_WIDTH, drag.current.startWidth + e.clientX - drag.current.startX)
				)
			)
		},
		[onResize]
	)

	useEffect(() => {
		const up = () => {
			drag.current = null
			document.body.style.cursor = ''
			document.body.style.userSelect = ''
		}
		window.addEventListener('mousemove', move)
		window.addEventListener('mouseup', up)
		return () => {
			window.removeEventListener('mousemove', move)
			window.removeEventListener('mouseup', up)
		}
	}, [move])

	return (
		<Box
			data-testid="artemis-agent-resize-handle"
			role="separator"
			aria-orientation="vertical"
			onMouseDown={(e) => {
				drag.current = { startX: e.clientX, startWidth: width }
				document.body.style.cursor = 'col-resize'
				document.body.style.userSelect = 'none'
			}}
			sx={{
				position: 'absolute',
				top: 0,
				right: -3,
				height: '100%',
				width: 6,
				cursor: 'col-resize',
				zIndex: 2,
				'&:hover': { bgcolor: 'primary.light', opacity: 0.4 },
			}}
		/>
	)
}

type UIProps = {
	expanded: boolean
	topOffset: string
	hasUnread: boolean
	items: ArtemisAgentItem[]
	input: string
	thinking: boolean
	errorText: string | null
	confirmation: ArtemisAgentConfirmation | null
	onExpand: () => void
	onCollapse: () => void
	onNewThread: () => void
	onInputChange: (value: string) => void
	onSend: (message: string) => void
	onConfirm: () => void
	onCancel: () => void
	'data-testid'?: string
}

/**
 * The assistant panel, docked between the sidebar and the page. Pure presentation; [ArtemisAgent] owns
 * the conversation.
 */
export function ArtemisAgentUI({
	expanded,
	topOffset,
	hasUnread,
	items,
	input,
	thinking,
	errorText,
	confirmation,
	onExpand,
	onCollapse,
	onNewThread,
	onInputChange,
	onSend,
	onConfirm,
	onCancel,
	...rest
}: UIProps) {
	const testId = getTestId(rest, 'artemis-agent')
	const [width, setWidth] = useState(DEFAULT_WIDTH)
	const bottomRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
	}, [items.length, thinking, confirmation])

	const column = {
		marginTop: topOffset,
		height: `calc(100vh - ${topOffset})`,
		flexShrink: 0,
		borderRight: 1,
		borderColor: 'divider',
		bgcolor: 'background.paper',
	}

	if (!expanded) {
		return (
			<Box
				data-testid={`${testId}-rail`}
				role="button"
				tabIndex={0}
				onClick={onExpand}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						onExpand()
					}
				}}
				sx={{
					...column,
					width: COLLAPSED_WIDTH,
					cursor: 'pointer',
					display: 'flex',
					justifyContent: 'center',
					pt: 2,
					'&:hover': { bgcolor: 'action.hover' },
				}}
			>
				<Tooltip title={`Open ${ARTY_NAME}`} placement="right">
					<IconButton size="small" data-testid={`${testId}-open`} sx={{ alignSelf: 'flex-start' }}>
						<Badge color="error" variant="dot" overlap="circular" invisible={!hasUnread}>
							<FontAwesomeIcon icon={faSparkles} style={{ fontSize: '1.1rem' }} />
						</Badge>
					</IconButton>
				</Tooltip>
			</Box>
		)
	}

	const send = () => {
		if (input.trim() && !thinking) onSend(input.trim())
	}

	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			send()
		}
	}

	return (
		<Box
			data-testid={testId}
			sx={{ ...column, position: 'relative', width, display: 'flex', flexDirection: 'column' }}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					px: 1.5,
					py: 1,
					borderBottom: 1,
					borderColor: 'divider',
					bgcolor: 'primary.main',
					color: 'primary.contrastText',
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<FontAwesomeIcon icon={faSparkles} style={{ fontSize: '0.95rem' }} />
					<Typography variant="subtitle2">{ARTY_NAME}</Typography>
				</Stack>
				<Stack direction="row" spacing={0.5}>
					<Tooltip title="New thread">
						<IconButton
							size="small"
							onClick={onNewThread}
							sx={{ color: 'inherit' }}
							data-testid={`${testId}-new-thread`}
							aria-label="New thread"
						>
							<FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '0.9rem' }} />
						</IconButton>
					</Tooltip>
					<Tooltip title="Collapse">
						<IconButton
							size="small"
							onClick={onCollapse}
							sx={{ color: 'inherit' }}
							data-testid={`${testId}-collapse`}
							aria-label={`Collapse ${ARTY_NAME}`}
						>
							<FontAwesomeIcon icon={faXmark} style={{ fontSize: '0.9rem' }} />
						</IconButton>
					</Tooltip>
				</Stack>
			</Stack>

			{errorText && (
				<Alert severity="error" sx={{ borderRadius: 0 }} data-testid={`${testId}-error`}>
					{errorText}
				</Alert>
			)}

			{items.length === 0 && !thinking ? (
				<Box
					data-testid={`${testId}-empty-state`}
					sx={{
						flex: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						px: 3,
						textAlign: 'center',
						color: 'text.secondary',
					}}
				>
					<Stack spacing={2} alignItems="center">
						<Typography variant="subtitle1" color="text.primary">
							How can I help you today?
						</Typography>
						<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
							{SUGGESTIONS.map((s) => (
								<Chip
									key={s.id}
									label={s.label}
									variant="outlined"
									clickable
									onClick={() => onSend(s.prompt)}
									data-testid={`${testId}-suggestion-${s.id}`}
								/>
							))}
						</Stack>
					</Stack>
				</Box>
			) : (
				<Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }} data-testid={`${testId}-thread`}>
					<Stack spacing={1.5}>
						{items.map((item, i) =>
							item.kind === 'tool' ? (
								// eslint-disable-next-line react/no-array-index-key
								<ToolActivityLine key={i} item={item} />
							) : (
								// eslint-disable-next-line react/no-array-index-key
								<MessageBubble key={i} role={item.role} content={item.content} />
							)
						)}
						{confirmation && (
							<ConfirmationCard
								confirmation={confirmation}
								loading={thinking}
								onConfirm={onConfirm}
								onCancel={onCancel}
							/>
						)}
						{thinking && <ThinkingIndicator />}
						<div ref={bottomRef} />
					</Stack>
				</Box>
			)}

			<Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, bgcolor: 'background.paper' }}>
				<TextField
					fullWidth
					multiline
					minRows={1}
					maxRows={6}
					size="small"
					placeholder={`Ask ${ARTY_NAME}…`}
					value={input}
					onChange={(e) => onInputChange(e.target.value)}
					onKeyDown={onKeyDown}
					slotProps={{
						htmlInput: { 'data-testid': `${testId}-input` },
						input: {
							endAdornment: (
								<Tooltip title="Send">
									<span>
										<IconButton
											size="small"
											color="primary"
											onClick={send}
											disabled={!input.trim() || thinking}
											data-testid={`${testId}-send`}
											aria-label="Send message"
										>
											<FontAwesomeIcon icon={faPaperPlaneTop} style={{ fontSize: '0.9rem' }} />
										</IconButton>
									</span>
								</Tooltip>
							),
						},
					}}
				/>
			</Box>

			<ResizeHandle width={width} onResize={setWidth} />
		</Box>
	)
}

type Props = {
	topOffset: string
}

/** Remembers the open conversation across reloads; the conversation itself lives in the API. */
const CONVERSATION_KEY = 'artemis-agent-conversation'

/**
 * The chat assistant (art-harness, served by the API at /agent). The API keeps the conversation: the
 * panel sends what the user typed (or their answer to a confirmation) and renders what comes back.
 */
export default function ArtemisAgent({ topOffset }: Props) {
	const enabled = useBoolVariation('feature-artemis-agent', false)
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const location = useLocation()
	const [expanded, setExpanded] = useState(false)
	const [hasUnread, setHasUnread] = useState(false)
	const [input, setInput] = useState('')
	const [conversationId, setConversationId] = useState(() => localStorage.getItem(CONVERSATION_KEY))
	const [latest, setLatest] = useState<AgentConversation | null>(null)

	// After a reload, reopen the conversation the user had open.
	const { data: reopened } = useQuery({
		queryKey: ArtemisAgentKeys.conversation(conversationId ?? ''),
		queryFn: () => ArtemisAgentApi.conversation(conversationId ?? ''),
		enabled: enabled && !!conversationId && !latest,
		retry: false,
	})

	const remember = (id: string | null) => {
		setConversationId(id)
		if (id) localStorage.setItem(CONVERSATION_KEY, id)
		else localStorage.removeItem(CONVERSATION_KEY)
	}

	const { mutate, isPending, variables, error, reset } = useMutation({
		mutationFn: ArtemisAgentApi.run,
		onSuccess: (res) => {
			setLatest(res)
			remember(res.conversationId)
			if (!expanded) setHasUnread(true)
			// Generic on purpose: tools decide what to show; the panel only knows how to navigate and reload.
			res.actions?.forEach((action) => {
				if (action.type === 'refresh') queryClient.invalidateQueries()
				if (action.type === 'navigate' && 'path' in action && action.path.startsWith('/app/')) {
					navigate({
						pathname: action.path,
						search: new URLSearchParams(action.query ?? {}).toString(),
					})
				}
			})
		},
	})

	if (!enabled) return null

	const conversation = latest ?? reopened ?? null
	const context = {
		path: location.pathname,
		query: Object.fromEntries(new URLSearchParams(location.search)),
	}

	const send = (text: string) => {
		if (isPending) return
		setInput('')
		mutate({ conversationId, message: text, context })
	}

	const answer = (approve: boolean) => mutate({ conversationId, approve, context })

	const newThread = () => {
		setLatest(null)
		remember(null)
		setInput('')
		reset()
	}

	// Show the user's message straight away, while the agent works on it.
	const sending = isPending && variables.message
	const items: AgentItem[] = [
		...(conversation?.items ?? []),
		...(sending
			? [{ kind: 'message' as const, role: 'user' as const, content: variables.message ?? '' }]
			: []),
	]
	const pending = isPending ? null : (conversation?.pendingConfirmation ?? null)

	// The shared api client turns network failures into 'VPN', which the global toast hides.
	const unreachable = error?.message === 'VPN'
	const errorText = unreachable
		? "Can't reach Artemis. Check that the API is running."
		: (error?.message ?? null)

	return (
		<ArtemisAgentUI
			expanded={expanded}
			topOffset={topOffset}
			hasUnread={hasUnread}
			items={items}
			input={input}
			thinking={isPending}
			errorText={errorText}
			confirmation={
				pending && {
					toolName: pending.tool,
					summary: pending.summary,
					arguments: pending.arguments,
				}
			}
			onExpand={() => {
				setExpanded(true)
				setHasUnread(false)
			}}
			onCollapse={() => setExpanded(false)}
			onNewThread={newThread}
			onInputChange={setInput}
			onSend={send}
			onConfirm={() => answer(true)}
			onCancel={() => answer(false)}
		/>
	)
}
